/* eslint-disable react/jsx-wrap-multilines */
import React, { useEffect, useCallback, useState, useMemo } from "react";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
import { useQuery, useLazyQuery } from "@apollo/client";

// Atoms
import { DashboardWebLink, Loading } from "@atoms/index";

// Assets
import { concretingRequest } from "@assets/applications/index";

// Molecules
import {
  PageHeader,
  BaseCard,
  BaseCardItem,
  BaseCardHeader
} from "@molecules/index";
import type {
  IConcretingRequestConcreteRecipeData,
  IConcretingRequestConcretingData,
  IConcretingRequestPumpData
} from "@molecules/index";

// graphql
import {
  TConcretingRequestAllWithPaginationQueries,
  concretingRequestAllWithPagination
} from "@graphql/groups/concretingRequest/query.graphql";
import {
  ConcretingRequestQueriesGetAllWithPaginationArgs,
  ConcretingRequestType,
  ECommandResultStatus,
  EWhereClausesOperators,
  PermissionUserQueriesGetAllWithoutPaginationArgs
} from "@store/graphql/helpers/types.graphql";
import {
  permissionUserAllWithoutPagination,
  TPermissionAllWithoutPaginationQueries
} from "@graphql/groups/permissionUser/query.graphql";

// Constants
import { ModulesID } from "src/store/constants";

// Hooks
import { useAuth } from "src/hooks/useAuth";
import { useSelectClient } from "@atoms/SelectClient";

import useTranslation from "@languages/useTranslation";
import { GlobalMessages } from "@languages/interfaces";
import * as Styled from "./styles";

const DashboardWebConcretingRequests: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();

  const { value: selectedClient } = useSelectClient();
  const [concretingRequests, setConcretingRequests] = useState<ConcretingRequestType[]>();
  const [whereContractIds, setWhereContractIds] = useState<string>("");

  const { getDecodedToken } = useAuth();
  const token = getDecodedToken();

  const [
    getPermissions,
    {
      loading: loadingPermissions,
      error: errorPermissions,
      data: dataPermissions
    }
  ] = useLazyQuery<
    TPermissionAllWithoutPaginationQueries,
    PermissionUserQueriesGetAllWithoutPaginationArgs
  >(permissionUserAllWithoutPagination(["contract.id"]), {
    fetchPolicy: "network-only",
    variables: {
      where: [
        {
          property: "user.id",
          operator: EWhereClausesOperators.EQUALS,
          value: token.primarysid
        },
        {
          property: "construction.client.id",
          operator: EWhereClausesOperators.EQUALS,
          value: selectedClient.id
        },
        {
          property: "module.id",
          operator: EWhereClausesOperators.EQUALS,
          value: ModulesID.CONTRACT
        }
      ]
    }
  });

  const [
    getConcretingRequests,
    {
      loading: loadingConcretingRequests,
      error: errorConcretingRequests,
      data: dataConcretingRequests
    }
  ] = useLazyQuery<
    TConcretingRequestAllWithPaginationQueries,
    ConcretingRequestQueriesGetAllWithPaginationArgs
  >(
    concretingRequestAllWithPagination([
      "id",
      "status",
      "requestedDate",
      "requestedQuantity",
      "quantityPerConcreteMixer",
      "toConfirm",
      "travelsReleased",
      "interval",
      "pieceToBeConcreted",
      "pump",
      "observationPump",
      "observationConcreting",
      "requester",
      "requesterPhoneNumber",
      "contract.code",
      "concreteBatchingPlant.name",
      "contractConcreteRecipe.concreteRecipe.description",
      "contract.construction.name",
      "contract.construction.address",
      "contract.construction.county",
      "contract.construction.number",
      "contract.construction.complement",
      "contract.contractSellers.seller.name"
    ]),
    {
      fetchPolicy: "network-only"
    }
  );

  const loadConcretingRequests = useCallback(async () => {
    const response = dataConcretingRequests?.concretingRequest.getAllWithPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {
      const concretingRequestsSorted = response.result
        ?.sort((a, b) => (a?.requestedDate < b?.requestedDate ? 1 : -1))
        .slice(0, 5);

      setConcretingRequests(
        concretingRequestsSorted as ConcretingRequestType[]
      );
    }
  }, [dataConcretingRequests, setConcretingRequests]);

  const makeWhereContractIds = useCallback(() => {
    const response = dataPermissions?.permissionUser.getAllWithoutPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {
      const contractIds: string[] = [];

      response.result?.forEach((permission) => {
        contractIds.push(permission?.contract?.id || "");
      });

      const contractIdsStr = contractIds
        .filter((id) => id && id !== "")
        .join("|");

      if (!contractIdsStr || contractIdsStr === "") {
        setWhereContractIds("");
        setConcretingRequests([]);

        return;
      }

      setWhereContractIds(contractIdsStr);
    }
  }, [dataPermissions]);

  useEffect(() => {
    if (whereContractIds !== "") {
      getConcretingRequests({
        variables: {
          where: [
            {
              property: "contract.id",
              operator: EWhereClausesOperators.IN,
              value: whereContractIds
            }
          ]
        }
      });
    }
  }, [getConcretingRequests, whereContractIds]);

  useEffect(() => {
    if (dataPermissions) makeWhereContractIds();
    if (errorPermissions) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorPermissions.message
      });
    }
  }, [dataPermissions, errorPermissions, addToast, makeWhereContractIds]);

  useEffect(() => {
    if (dataConcretingRequests) loadConcretingRequests();
    if (errorConcretingRequests) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorConcretingRequests.message
      });
    }
  }, [
    addToast,
    dataConcretingRequests,
    errorConcretingRequests,
    loadConcretingRequests
  ]);

  useEffect(() => {
    setConcretingRequests([]);
  }, [selectedClient, setConcretingRequests]);

  useEffect(() => {
    const interval = setInterval(() => {
      setWhereContractIds("");
      getPermissions();
    }, 600000);

    return () => {
      clearInterval(interval);
    };
  }, [getPermissions]);

  useEffect(() => {
    getPermissions();
  }, [getPermissions]);

  const concretingRequestDetails = useCallback(
    (concretingRequest: ConcretingRequestType) => {
      const concretingRequestConcreting: IConcretingRequestConcretingData = {
        status: concretingRequest.status,
        requestDate: concretingRequest.requestedDate,
        concreteBatchingPlantName:
          concretingRequest.concreteBatchingPlant?.name ?? "",
        // toConfirm: concretingRequest.toConfirm,
        // travelsReleased: concretingRequest.travelsReleased ?? undefined,
        interval: concretingRequest.interval,
        observationConcreting: concretingRequest.observationConcreting ?? "",
        requester: concretingRequest.requester,
        requesterPhoneNumber: concretingRequest.requesterPhoneNumber
      };

      const concretingRequestConcreteRecipe: IConcretingRequestConcreteRecipeData = {
        concreteRecipeDescription:
            concretingRequest.contractConcreteRecipe?.concreteRecipe
              ?.description ?? "",
        quantityPerConcreteMixer: concretingRequest.quantityPerConcreteMixer,
        pieceToBeConcreted: concretingRequest.pieceToBeConcreted ?? undefined,
        requestedQuantity: concretingRequest.requestedQuantity,
        status: concretingRequest.status
      };

      const concretingRequestPump: IConcretingRequestPumpData = {
        pump: concretingRequest.pump,
        pipingDistance: concretingRequest.pipingDistance,
        observationPump: concretingRequest.observationPump ?? undefined,
        status: concretingRequest.status
      };

      return (
        <div>
          <BaseCardItem
            type="concretingRequestConcreting"
            concretingRequestConcreting={concretingRequestConcreting}
          />
          <BaseCardItem
            type="concretingRequestConcreteRecipe"
            concretingRequestConcreteRecipe={concretingRequestConcreteRecipe}
          />
          <BaseCardItem
            type="concretingRequestPump"
            concretingRequestPump={concretingRequestPump}
          />
        </div>
      );
    },
    []
  );

  return (
    <Styled.Container>
      <DashboardWebLink
        icon={concretingRequest}
        title={t(GlobalMessages.lastConcretingRequestsText)}
        link="/concreting-requests"
      />

      <Styled.Content>
        {(loadingConcretingRequests || loadingPermissions) && (
          <Loading loading />
        )}
        {!loadingPermissions
          && !loadingConcretingRequests
          && concretingRequests
          && concretingRequests.map((concretingRequest) => (
            <BaseCard
              key={concretingRequest.id}
              id={concretingRequest.id}
              header={
                <BaseCardHeader
                  type="concretingRequest"
                  data={concretingRequest}
                />
                }
              details={concretingRequestDetails(concretingRequest)}
            />
          ))}
      </Styled.Content>
    </Styled.Container>
  );
};

export default DashboardWebConcretingRequests;
